import {IParams} from "../base/endpoints/iparams";
import {PostTypes} from "../modules/webapp/models/posts/post-types.enum";
import {BaseEndpoint} from "../base/endpoints/base.endpoint";
import {OrderStatus} from "../modules/dash/domains/sales/orders/models/order.model";

export class EventKey {
  static ERRORS = {
    HTTP_UNAUTHORIZED_ERROR: "error:http:unauthorized"
  }

  static AUTH = {
    LOGIN_SUCCESS: 'auth:login:success',
    SERVER_ERROR: 'auth:login:server_error',
    ERROR_INVALID_CREDENTIALS: 'auth:login:invalid_credentials',
    CHANGE_PASSWORD: 'auth:change-password'
  }

  static REGISTRATION = {
    OTP_SENT: 'register:otp:sent',
    OTP_SEND_ERROR: "register:otp:send-error",
    REGISTER_SUCCESS: 'register:success',
    REGISTER_ERROR: 'register:error'
  }

  static RESET_PASSWORD = {
    TOKEN_REQUESTED: 'reset:otp:token-requested',
    RESET_SUCCESS: 'reset:password:success',
    RESET_PASSWORD_ERROR: 'reset:password:error'
  }

  static CATEGORIES = {
    LOADED_ROOT_CATEGORIES(): string {
      return 'categories:load:success:root'
    },
    LOADED_CHILD_CATEGORIES(): string {
      return 'categories:load:success:child'
    },
    INTERESTS_SAVED() {
      return "categories:interests:saved";
    },
    FETCH_INTERESTS() {
      return "categories:interests:fetch";
    },
    LOADED_CATEGORY_ATTRIBUTES(categoryIds: number[]) {
      return "categories:attributes:cat" + categoryIds;
    },
    SEARCH_CATEGORIES() {
      return "categories:search";
    },
    SEARCH_CATEGORIES_FOR_FILTER() {
      return "categories:search:filter-search";
    },
    GET_MULTIPLE(ids: number[]): string {
      return 'categories:get-multiple:' + ids.join(',')
    }
  }

  static PRODUCT_CATEGORIES = {
    SEARCH(params: IParams) {
      return "categorys:search:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'categorys:upsert:' + exId
    },
    GET(exId: number): string {
      return 'categorys:get-single:' + exId
    }
  }

  static PRODUCTS = {
    SEARCH_PRODUCTS(): string {
      return 'product:search'
    },
    SEARCH_BRANDS(): string {
      return 'product:search-brands'
    },
    PRODUCTS_LOADED(attr: number | null): string {
      return 'products:load:success:attr' + attr
    },
    PRODUCT_DETAILS_LOADED(slug: string): string {
      return 'products:load:details:success:' + slug
    }
  }

  static PRODUCTS_V2 = {
    SEARCH(params: IParams) {
      return "products:search:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'products:upsert:' + exId
    },
    GET(exId: number): string {
      return 'products:get-single:' + exId
    },
    APPROVE(id: number): string {
      return 'products:approve-single:' + id
    }
  }

  static ATTRS = {
    ATTR_FOR_PRODUCT_LOADED(productId: number): string {
      return 'attrs:load:success:' + productId
    }
  }

  static FILES = {
    IMAGE_UPLOAD(uniqueKey: string): string {
      return 'files:upload:images:' + uniqueKey
    },
    SEARCH(params: IParams) {
      return "files:search:search".concat(BaseEndpoint.toParamString(params))
    },
    SEARCH_IMAGES(params: IParams) {
      return "files:images:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'files:upsert:' + exId
    },
    GET(exId: number): string {
      return 'files:get-single:' + exId
    }
  }

  static REVIEWS = {
    POST_REVIEW(productId: number): string {
      return 'reviews:create-review' + productId
    },
    LOAD_MY_REVIEW(productId: number) {
      return "reviews:fetch-my-review:" + productId;
    },
    LOAD_REVIEW(reviewSlug: string) {
      return "reviews:fetch-review:" + reviewSlug;
    },
    LOADED_PRODUCT_REVIEWS(productId: number) {
      return "reviews:loaded-product-reviews:" + productId;
    },
    FETCH_USER_REVIEWS(username: string) {
      return "reviews:loaded-user-reviews:" + username;
    },
    LOADED_PRODUCT_AVG_RATINGS() {
      return "reviews:loaded-product-ratings:avg";
    },
    MY_REVIEW_POSTED(productId: number) {
      return "review:post:my-review:p:" + productId;
    },
    FETCH_RECENT_REVIEWS() {
      return "reviews:recent-reviews";
    },
    FETCH_CRITIC_REVIEWS() {
      return "reviews:critic-reviews";
    },
    FETCH_INTERESTING_STORIES() {
      return "reviews:interesting-stories";
    },
    CLOSE_SHOW_REVIEW_DIALOG() {
      return "close-dialog";
    },
    SEND_VERIFICATION_REQUEST(reviewId: number) {
      return "review:send-verification-request:" + reviewId
    },
    POST_REVIEW_REPORT(reviewId: number) {
      return "report:send-report-reviews-request:" + reviewId
    },
    GET_VERIFICATION_FOR_REVIEW(reviewId: number) {
      return "review:get-verification-for-review:" + reviewId;
    },
    RECONSIDER_VERIFICATION(verificationId: number) {
      return "review:reconsider-verification:" + verificationId;
    },
    GET_REPORT_DETAILS_FOR_REVIEW(reviewId: number | null) {
      return "report:report-review-details:" + reviewId;
    },
    GET_REPORT_EXISTS(postId: number | null) {
      return "report:report-exists-for-post:" + postId;
    }
  }

  static POSTS = {
    FETCH_NEWS_FEED_POSTS(type: PostTypes): string {
      return 'posts:posts:newsfeed:fetch' + type
    },
    FETCH_COMMENTS(postId: number): string {
      return 'posts:comments:fetch:' + postId;
    },
    FETCH_ERROR() {
      return 'posts:fetch:error'
    },
    SUBMIT_ERROR() {
      return 'posts:submit:error'
    },
    SUBMIT_POST(parentId: number | null, content: string) {
      return 'posts:submit:success:' + parentId + ':c:' + content
    },
    SUBMIT_REACTION(postId: number) {
      return 'reaction:submit:success:' + postId
    },
    FETCH_POST(id: number) {
      return "post:fetch:single:" + id
    },
    FETCH_POST_REACTIONS(postId: number) {
      return "post:fetch:reactions:for:" + postId;
    },
    FETCH_REACTIONS() {
      return "post:fetch:reactions";
    },
    FETCH_USER_POSTS(username: string) {
      return "posts:fetch:user-posts:" + username;
    },
    UPDATE_POST() {
      return "post:update";
    },
    DELETE_POST(id: number) {
      return "post:delete:" + id;
    },
    FETCH_TRENDING_HASHTAGS() {
      return "posts:fetch-trending-hashtags"
    },

    SEARCH(params: IParams) {
      return "posts:search:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'posts:upsert:' + exId
    },
    GET(exId: number): string {
      return 'posts:get-single:' + exId
    }
  }

  static NOTIFICATIONS = {
    SEARCH(): string {
      return 'notifications:search'
    },
    GET(exId: number): string {
      return 'notifications:get-single:' + exId
    },
    UPSERT(exId: number | null): string {
      return 'notifications:upsert:' + exId
    },
    RESPOND(id: number): string {
      return 'notifications:respond:' + id
    },
    FETCH_MY_NOTIFICATIONS(): string {
      return 'notifications:fetch-my-notifications'
    },
    MARK_READ(): string {
      return 'notifications:mark-read'
    },
    MAKE_STICKY(): string {
      return 'notifications:make-sticky'
    },
    PUBLISH(): string {
      return 'notifications:publish'
    },
    INCREASE_VIEW_COUNT(id: number): string {
      return 'notifications:increase-read-count-' + id
    },
    NOTIFY(id: number): string {
      return 'notifications:notify-' + id
    },
    INCREASE_CLICK_COUNT(id: number): string {
      return 'notifications:increase-click-count-' + id
    },
    COUNT_UNREAD(): string {
      return 'notifications:count-unread'
    }
  }

  static LEADERBOARDS = {
    FETCH_LEADERBOARD(username: string | null): string {
      return 'leaderboards:fetch:' + username
    },
    FETCH_MY_LEADERBOARD(): string {
      return 'leaderboards:fetch:self'
    },
    FETCH_ERROR(): string {
      return 'leaderboards:fetch:error'
    },
    REDEEM_SUCCESS() {
      return 'leaderboard:redeem:success'
    },
    REDEEM_SUCCESS_NOTIFY_PROFILE() {
      return 'leaderboard:redeem:success:notify-profile'
    },
    REDEEM_ERROR() {
      return 'leaderboard:redeem:error'
    },
    FETCH_RECENT_ACHIEVEMENTS() {
      return "leaderboard:achievements:fetch-recent";
    }
  }
  static VENDORS = {
    SEARCH_VENDORS(): string {
      return 'vendors:search'
    },
    FETCH_ERROR(): string {
      return 'vendors:fetch:error'
    },
    SEARCH(params: IParams) {
      return "vendors:search:search".concat(BaseEndpoint.toParamString(params))
    },
    MY_VENDORS(params: IParams) {
      return "vendors:my-vendors:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'vendors:upsert:' + exId
    },
    GET(exId: number): string {
      return 'vendors:get-single:' + exId
    },
    TOGGLE_APPROVAL(id: number): string {
      return 'vendors:toggle-approval:' + id
    }
  };

  static WALLET = {
    FETCH_MY_WALLET(): string {
      return 'wallet:fetch-my-wallet:success'
    },
    FETCH_ERROR() {
      return 'wallet:fetch:error'
    },
    FETCH_TRANSACTIONS() {
      return 'wallet:fetch:transactions'
    }
  }

  static USERS = {
    FETCH_FOLLOW_STATS(username: string) {
      return 'follow:stats:' + username
    },
    FOLLOW_USER(username: string) {
      return "follow:user:" + username;
    },
    UNFOLLOW_USER(username: string) {
      return "unfollow:user:" + username;
    },
    GET_MY_FOLLOWERS() {
      return "follow:followers:find-followers";
    },
    GET_MY_PROFILE() {
      return "user:profile:get-my-profile"
    },
    UPDATE_MY_PROFILE() {
      return "user:profile:update-my-profile"
    },
    DELETE_ME() {
      return 'users:delete-me'
    },
    SEARCH_USERS() {
      return 'users:search-users'
    },
    SEARCH(params: IParams) {
      return "users:search:search".concat(BaseEndpoint.toParamString(params))
    },
    SEARCH_CUSTOMERS(params: IParams) {
      return "users:search:search-customers".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'users:upsert:' + exId
    },
    GET(exId: number): string {
      return 'users:get-single:' + exId
    },
    CHECK_USERNAME(params: IParams): string {
      return "users:check-username" + BaseEndpoint.toParamString(params)
    }
  }

  static REQUEST_CREDENTIALS = {
    ADMIN_SEARCH_LOCKOUTS(): string {
      return "req:cred:admin-search-lockouts"
    }
  }
  static ACTIVITY_LOGS = {
    ADMIN_SEARCH(): string {
      return "activity:logs:admin-search"
    }
  }

  static POLL_STATS = {
    OVERVIEW(): string {
      return "poll:stats:overview"
    },
    DATE_WISE_POLL_COUNT(): string {
      return "poll:stats:date-wise-poll-count"
    }
  }

  static CRUDEXAMPLES = {
    SEARCH(params: IParams) {
      return "crudexamples:search:".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'crudexamples:upsert:' + exId
    },
    GET(exId: number): string {
      return 'crudexamples:get-single:' + exId
    }
  }

  static QUESTIONNAIRES = {
    GET_DETAILS(attr: number | null): string {
      return 'questionnaires:details' + attr
    },
    UPDATE(attr: number | null): string {
      return 'questionnaires:update' + attr
    },
    CREATE(title: string): string {
      return 'questionnaires:create' + title
    },
    TOGGLE_APPROVAL(qid: number): string {
      return 'questionnaires:toggle-approval' + qid
    },
    FETCH_RESULT_SHEET(qid: number): string {
      return 'questionnaires:result-sheet:' + qid
    },
    SEARCH(params: IParams) {
      return "questionnaires:search:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'questionnaires:upsert:' + exId
    },
    GET(exId: number): string {
      return 'questionnaires:get-single:' + exId
    },
    GENERATE_QUESTION_REVISIONS_FOR_LABEL(labelId: number): string {
      return 'questionnaires:generate-question-revisions-for-label:' + labelId
    }
  }

  static QUESTIONS = {
    SEARCH(params: IParams) {
      return "questions:search:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'questions:upsert:' + exId
    },
    GET(exId: number): string {
      return 'questions:get-single:' + exId
    },
    IMPORT(questionnaireId: number | null): string {
      return 'questions:import' + questionnaireId
    },
    SEARCH_REVISIONS(params: IParams): string {
      return 'revisions:search-revisions' + BaseEndpoint.toParamString(params)
    },
    REVISE_QUESTION(questionId: number | null): string {
      return 'revisions:revise-question' + questionId
    },
    GENERATE(questionnaireId: number | null): string {
      return 'revisions:generate' + questionnaireId
    },
    ACCEPT(revisionId: number): string {
      return 'revisions:accept' + revisionId
    },
    CREATE_QUESTIONS_FROM_REV(ids: number[]): string {
      return 'revisions:create-questions-from-rev' + ids.join(",")
    },
    UPSERT_REVISION(exId: number | null): string {
      return 'revisions:upsert-revision:' + exId
    },
    FETCH_REVISION(exId: number | null): string {
      return 'revisions:fetch-revision:' + exId
    },
    DELETE_REVISION(exId: number | null): string {
      return 'revisions:delete:' + exId
    },
  }

  static QUESTIONNAIREREVISIONS = {
    SEARCH(params: IParams) {
      return "questionnairerevisions:search:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'questionnairerevisions:upsert:' + exId
    },
    GET(exId: number): string {
      return 'questionnairerevisions:get-single:' + exId
    },
    ACCEPT(exIds: number[]): string {
      return 'questionnairerevisions:get-single:' + exIds.join(',')
    },
    GENERATE(labelId: number): string {
      return 'questionnairerevisions:generate-with-ai:' + labelId
    }
  }

  static POLLS = {
    SEARCH(): string {
      return 'polls:search'
    },
    GET_DETAILS(attr: number | null): string {
      return 'polls:details' + attr
    },
    UPDATE(attr: number | null): string {
      return 'polls:update' + attr
    },
    CREATE(title: string): string {
      return 'polls:create' + title
    },
    IMPORT(questionnaireId: number): string {
      return 'polls:import' + questionnaireId
    }
  }
  static VOTES = {
    SEARCH(): string {
      return 'votes:search'
    },
    FETCH_USER_VOTE_STATS(): string {
      return 'votes:user-vote-stats'
    },
    FETCH_POLL_VOTE_STATS(): string {
      return 'votes:poll-vote-stats'
    }
  }

  static POLL_OPTIONS = {
    SEARCH_LEGACY(): string {
      return 'poll-options:search'
    },
    SEARCH(params: IParams) {
      return "poptions:search:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'poptions:upsert:' + exId
    },
    GET(exId: number): string {
      return 'poptions:get-single:' + exId
    },
    GET_DETAILS(attr: number | null): string {
      return 'poll-options:details' + attr
    },
    UPDATE(attr: number | null): string {
      return 'poll-options:update' + attr
    },
    CREATE(title: string): string {
      return 'poll-options:create' + title
    }
  }

  static LABELS_LEGACY = {
    SEARCH(): string {
      return 'labels-legacy:search'
    },
    GET_LABEL_TREE(parentId: number): string {
      return 'labels-legacy:search' + parentId
    },
    GET_DETAILS(attr: number | null): string {
      return 'labels-legacy:details' + attr
    },
    UPDATE(attr: number | null): string {
      return 'labels-legacy:update' + attr
    },
    CREATE(title: string): string {
      return 'labels-legacy:create' + title
    }
  }

  static LABELS = {
    SEARCH(params: IParams) {
      return "labels:search:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'labels:upsert:' + exId
    },
    GET(exId: number): string {
      return 'labels:get-single:' + exId
    },
    GET_MULTIPLE(ids: number[]): string {
      return 'labels:get-multiple:' + ids.join(',')
    }
  }

  static FEEDBACKS = {
    SEARCH(params: IParams) {
      return "feedbacks:search:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'feedbacks:upsert:' + exId
    },
    GET(exId: number): string {
      return 'feedbacks:get-single:' + exId
    },
    TAKE_ACTION(id: number): string {
      return 'feedbacks:take-action:' + id
    }
  }

  static ATTRIBUTES = {
    SEARCH(params: IParams) {
      return "attributes:search:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'attributes:upsert:' + exId
    },
    GET(exId: number): string {
      return 'attributes:get-single:' + exId
    }
  }

  static FILTERABLEATTRIBUTES = {
    SEARCH(params: IParams) {
      return "filterableattributes:search:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'filterableattributes:upsert:' + exId
    },
    GET(exId: number): string {
      return 'filterableattributes:get-single:' + exId
    }
  }

  static BRANDS = {
    SEARCH(params: IParams) {
      return "brands:search:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'brands:upsert:' + exId
    },
    GET(exId: number): string {
      return 'brands:get-single:' + exId
    }
  }

  static ACVALIDATIONTOKENS = {
    SEARCH(params: IParams) {
      return "acvalidationtokens:search:search".concat(BaseEndpoint.toParamString(params))
    }
  }

  static VARIATIONS = {
    SEARCH(params: IParams) {
      return "variations:search:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'variations:upsert:' + exId
    },
    GET(exId: number): string {
      return 'variations:get-single:' + exId
    }
  }

  static PRODUCTSPECIFICATIONS = {
    SEARCH(params: IParams) {
      return "productspecifications:search:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'productspecifications:upsert:' + exId
    },
    GET(exId: number): string {
      return 'productspecifications:get-single:' + exId
    }
  }

  static STOCKS = {
    SEARCH(params: IParams) {
      return "stocks:search:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'stocks:upsert:' + exId
    },
    GET(exId: number): string {
      return 'stocks:get-single:' + exId
    },
    GET_BY_SKU(sku: string): string {
      return 'stocks:get-single-by-sky:' + sku
    },

    GET_BARCODE(stockId: number): string {
      return 'stocks:get-barcode:' + stockId
    },

  }
  static STOCK_HISTORIES = {
    SEARCH(params: IParams) {
      return "stockhistorys:search:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'stockhistorys:upsert:' + exId
    },
    GET(exId: number): string {
      return 'stockhistorys:get-single:' + exId
    }
  }

  static LOCATIONTYPES = {
    SEARCH(params: IParams) {
      return "locationtypes:search:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'locationtypes:upsert:' + exId
    },
    GET(exId: number): string {
      return 'locationtypes:get-single:' + exId
    }
  }

  static LOCATIONS = {
    SEARCH(params: IParams) {
      return "locations:search:search".concat(BaseEndpoint.toParamString(params))
    },
    GET_BY_TYPE(typeId: number): string {
      return 'locations:get-by-type:' + typeId
    },
    UPSERT(exId: number | null): string {
      return 'locations:upsert:' + exId
    },
    GET(exId: number): string {
      return 'locations:get-single:' + exId
    },
    GET_MULTIPLE(ids: number[]): string {
      return 'locations:get-multiple:' + ids.join(',')
    }
  }

  static ADDRESSS = {
    SEARCH(params: IParams) {
      return "addresss:search:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'addresss:upsert:' + exId
    },
    GET(exId: number): string {
      return 'addresss:get-single:' + exId
    }
  }

  static ORDERS = {
    SEARCH(params: IParams) {
      return "orders:search:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'orders:upsert:' + exId
    },
    GET(exId: number): string {
      return 'orders:get-single:' + exId
    },
    MAKE_PAYMENT(orderId: number): string {
      return 'orders:make-payment:' + orderId
    },
    CONFIRM_ORDER(orderId: number): string {
      return 'orders:confirm-order:' + orderId
    },
    DOWNLOAD_RECEIPT(orderId: number): string {
      return 'orders:download-receipt:' + orderId
    },
    CHANGE_ORDER_STATUS(orderId: number, status: OrderStatus): string {
      return 'orders:change-order-status:' + orderId + ":" + status
    },
    TOGGLE_PAYMENT_REMINDER(orderId: number, reminder: boolean) {
      return 'orders:payment-reminder:' + orderId + ":" + reminder
    }
  }

  static CUSTOMERS = {
    SEARCH(params: IParams) {
      return "customers:search:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'customers:upsert:' + exId
    },
    GET(exId: number): string {
      return 'customers:get-single:' + exId
    }
  }

  static STATISTICS = {
    SALES_STATS(params: IParams) {
      return "statistics:sales-stats" + BaseEndpoint.toParamString(params)
    },
    TOP_BUYING_CUSTOMERS(params: IParams) {
      return "statistics:top-buying-customers" + BaseEndpoint.toParamString(params)
    },
    TOP_DUE_CUSTOMERS(params: IParams) {
      return "statistics:top-due-customers" + BaseEndpoint.toParamString(params)
    },
    MOST_FREQUENT_CUSTOMERS(params: IParams) {
      return "statistics:most-frequent-customers" + BaseEndpoint.toParamString(params)
    },
    CUSTOMERS_WITH_GOOD_TRACT_RECORD(params: IParams) {
      return "statistics:customers-gtr" + BaseEndpoint.toParamString(params)
    },
    BUYING_SELLING_OVERVIEW(params: IParams) {
      return "statistics:buying-selling-overview" + BaseEndpoint.toParamString(params)
    },
    MOST_PROFITABLE_PRODUCTS(params: IParams) {
      return "statistics:top-products" + BaseEndpoint.toParamString(params)
    },
    PREDICTED_PRODUCTS(params: IParams) {
      return "statistics:predicted-products-products" + BaseEndpoint.toParamString(params)
    },
    LOW_STOCKS(params: IParams) {
      return "statistics:low-stocks" + BaseEndpoint.toParamString(params)
    },
  }

  static UNITS = {
    SEARCH(params: IParams) {
      return "units:search:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'units:upsert:' + exId
    },
    GET(exId: number): string {
      return 'units:get-single:' + exId
    }
  }

  static ORGANIZATIONS = {
    SEARCH(params: IParams) {
      return "organizations:search:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'organizations:upsert:' + exId
    },
    GET(exId: number): string {
      return 'organizations:get-single:' + exId
    },
    GET_MY_ORG(): string {
      return 'organizations:get-my-irg'
    }
  }

  static EMPLOYEES = {
    SEARCH(params: IParams) {
      return "employees:search:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: string | null): string {
      return 'employees:upsert:' + exId
    },
    GET(exId: string): string {
      return 'employees:get-single:' + exId
    },
    DELETE(exId: string): string {
      return 'employees:delete:' + exId
    }
  }

  static STAGES = {
    SEARCH(params: IParams) {
      return "stages:search:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'stages:upsert:' + exId
    },
    GET(exId: number): string {
      return 'stages:get-single:' + exId
    }
  }

  static LEADS = {
    SEARCH(params: IParams) {
      return "leads:search:search".concat(BaseEndpoint.toParamString(params))
    },
    FOLLOW_UPS(params: IParams) {
      return "leads:search:follow-ups".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'leads:upsert:' + exId
    },
    GET(exId: number): string {
      return 'leads:get-single:' + exId
    }
  }

  static LEADPROPAGATIONS = {
    SEARCH(params: IParams) {
      return "leadpropagations:search:search".concat(BaseEndpoint.toParamString(params))
    },
    EMPLOYEE_ACTIVITIES(params: IParams) {
      return "leadpropagations:employee-activities".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'leadpropagations:upsert:' + exId
    },
    GET(exId: number): string {
      return 'leadpropagations:get-single:' + exId
    }
  }

  static ACQUISITIONCAMPAIGNS = {
    SEARCH(params: IParams) {
      return "acquisitioncampaigns:search:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'acquisitioncampaigns:upsert:' + exId
    },
    GET(exId: number): string {
      return 'acquisitioncampaigns:get-single:' + exId
    }
  }

  static TASKS = {
    SEARCH(params: IParams) {
      return "tasks:search:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'tasks:upsert:' + exId
    },
    REMOVE_ASSIGNMENT(taskId: number, assigneeId: string): string {
      return 'tasks:upsert:' + taskId + ":a:" + assigneeId
    },
    GET(exId: number): string {
      return 'tasks:get-single:' + exId
    },
    SEARCH_ACTIVITIES(taskId: number): string {
      return 'task:activities-search:' + taskId
    }
  }

  static TASKSTATES = {
    SEARCH(params: IParams) {
      return "taskstates:search:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'taskstates:upsert:' + exId
    },
    GET(exId: number): string {
      return 'taskstates:get-single:' + exId
    }
  }

  static BOARDS = {
    SEARCH(params: IParams) {
      return "boards:search:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'boards:upsert:' + exId
    },
    GET(exId: number): string {
      return 'boards:get-single:' + exId
    }
  }

  static TASKASSIGNMENTS = {
    SEARCH(params: IParams) {
      return "taskassignments:search:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'taskassignments:upsert:' + exId
    },
    GET(exId: number): string {
      return 'taskassignments:get-single:' + exId
    }
  }
  static SEARCHHISTORIES = {
    SEARCH(params: IParams) {
      return "searchhistorys:search:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'searchhistorys:upsert:' + exId
    },
    GET(exId: number): string {
      return 'searchhistorys:get-single:' + exId
    }
  }
  static BLOGS = {
    SEARCH(params: IParams) {
      return "blogs:search:fdsf".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'blogs:upsert:' + exId
    },
    UPSERT_CONFIG(exId: number | null): string {
      return 'blogs:upsert-config:' + exId
    },
    GET(exId: number): string {
      return 'blogs:get-single:' + exId
    },
    GET_BY_SLUG(slug: string): string {
      return 'blogs:get-by-slug:' + slug
    },
    PUBLISH(blogId: number): string {
      return 'blogs:publish:' + blogId
    }
  }
  static POST_CATEGORIES = {
    SEARCH(params: IParams) {
      return "postcategories:search:search".concat(BaseEndpoint.toParamString(params))
    },
    SEARCH_PUBLIC(params: IParams) {
      return "postcategories:search:search-public".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'postcategories:upsert:' + exId
    },
    GET(exId: number): string {
      return 'postcategories:get-single:' + exId
    }
  }
  static BLOGPOSTS = {
    SEARCH(params: IParams) {
      return "blogposts:search:search".concat(BaseEndpoint.toParamString(params))
    },
    SEARCH_BY_CATEGORY(slug: string, params: IParams) {
      return "blogposts:search:search-by-category".concat(slug).concat(BaseEndpoint.toParamString(params))
    },
    SEARCH_BY_HASHTAG(hashtag: string, params: IParams) {
      return "blogposts:search:search-by-hashtag".concat(hashtag).concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'blogposts:upsert:' + exId
    },
    GET(exId: number): string {
      return 'blogposts:get-single:' + exId
    },
    GET_DETAILS(slug: string): string {
      return 'blogposts:get-single:' + slug
    },
    GET_TOP_HASHTAGS(blogSlug: string) {
      return "blogposts:get-top-hashtags:" + blogSlug
    }
  }
  static BLOGMENUS = {
    SEARCH(params: IParams) {
      return "blogmenus:search:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'blogmenus:upsert:' + exId
    },
    GET(exId: number): string {
      return 'blogmenus:get-single:' + exId
    }
  }

  static WEBPAGES = {
    SEARCH(params: IParams) {
      return "webpages:search:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'webpages:upsert:' + exId
    },
    GET(exId: number): string {
      return 'webpages:get-single:' + exId
    },
    PUBLISH(id: number): string {
      return 'webpages:publish:' + id
    }
  }

  static PRIVILEGES = {
    SEARCH(params: IParams) {
      return "privileges:search:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'privileges:upsert:' + exId
    },
    GET(exId: number): string {
      return 'privileges:get-single:' + exId
    },
    GET_ENDPOINTS(): string {
      return 'privileges:get-endpoints'
    }
  }

  static ANSWERS = {
    SEARCH(params: IParams) {
      return "answers:search:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'answers:upsert:' + exId
    },
    GET(exId: number): string {
      return 'answers:get-single:' + exId
    }
  }

  static ALLOWEDDOMAINS = {
    SEARCH(params: IParams) {
      return "alloweddomains:search:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'alloweddomains:upsert:' + exId
    },
    GET(exId: number): string {
      return 'alloweddomains:get-single:' + exId
    }
  }

  static CLIENTAPPS = {
    SEARCH(params: IParams) {
      return "clientapps:search:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'clientapps:upsert:' + exId
    },
    GET(exId: number): string {
      return 'clientapps:get-single:' + exId
    }
  }

  static BADGES = {
    SEARCH(params: IParams) {
      return "badges:search:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'badges:upsert:' + exId
    },
    GET(exId: number): string {
      return 'badges:get-single:' + exId
    }
  }

  static TRANSACTION_CATEGORIES = {
    SEARCH(params: IParams) {
      return "transaction_categories:search:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'transaction_categories:upsert:' + exId
    },
    GET(exId: number): string {
      return 'transaction_categories:get-single:' + exId
    }
  }

  static TOPBANNERS = {
    SEARCH(params: IParams) {
      return "topbanners:search:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'topbanners:upsert:' + exId
    },
    GET(exId: number): string {
      return 'topbanners:get-single:' + exId
    }
  }

  static BILLING_GATEWAYS = {
    SEARCH(params: IParams) {
      return "billing-gateways:search:".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: string | null): string {
      return 'billing-gateways:upsert:' + exId
    },
    GET(exId: string): string {
      return 'billing-gateways:get-single:' + exId
    },
    TEST(id: string): string {
      return 'billing-gateways:test:' + id
    }
  }

  static BILLING_ROUTING_RULES = {
    SEARCH(params: IParams) {
      return "billing-routing-rules:search:".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: string | null): string {
      return 'billing-routing-rules:upsert:' + exId
    },
    GET(exId: string): string {
      return 'billing-routing-rules:get-single:' + exId
    }
  }

  static BILLING_CUSTOMERS = {
    SEARCH(params: IParams) {
      return "billing-customers:search:".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: string | null): string {
      return 'billing-customers:upsert:' + exId
    },
    GET(exId: string): string {
      return 'billing-customers:get-single:' + exId
    }
  }

  static BILLING_PAYMENT_METHODS = {
    LIST(customerId: string): string {
      return 'billing-payment-methods:list:' + customerId
    },
    SET_DEFAULT(pmId: string): string {
      return 'billing-payment-methods:set-default:' + pmId
    },
    DELETE(pmId: string): string {
      return 'billing-payment-methods:delete:' + pmId
    }
  }

  static BILLING_TRANSACTIONS = {
    SEARCH(params: IParams) {
      return "billing-transactions:search:".concat(BaseEndpoint.toParamString(params))
    },
    GET(exId: string): string {
      return 'billing-transactions:get-single:' + exId
    },
    REFUND(txId: string): string {
      return 'billing-transactions:refund:' + txId
    },
    SUMMARY(): string {
      return 'billing-transactions:summary'
    }
  }

  static BILLING_API_KEYS = {
    SEARCH(params: IParams) {
      return "billing-api-keys:search:".concat(BaseEndpoint.toParamString(params))
    },
    CREATE(): string {
      return 'billing-api-keys:create'
    },
    REGENERATE(id: number): string {
      return 'billing-api-keys:regenerate:' + id
    },
    DELETE(id: number): string {
      return 'billing-api-keys:delete:' + id
    }
  }

  static BILLING_CONTENT_TEMPLATES = {
    SEARCH(params: IParams) {
      return "billing-content-templates:search:".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: string | null): string {
      return 'billing-content-templates:upsert:' + exId
    },
    GET(exId: string): string {
      return 'billing-content-templates:get-single:' + exId
    },
    DELETE(id: string): string {
      return 'billing-content-templates:delete:' + id
    },
  }

  static BILLING_PREPARED_CONTENTS = {
    SEARCH(params: IParams) {
      return "billing-prepared-contents:search:".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: string | null) {
      return 'billing-prepared-contents:upsert:' + exId
    },
    GET(exId: string): string {
      return 'billing-prepared-contents:get-single:' + exId
    },
    DELETE(id: string) {
      return 'billing-prepared-contents:delete:' + id
    },
    FINALIZE(id: string) {
      return 'billing-prepared-contents:finalize:' + id
    },
    MARK_DRAFT(id: string) {
      return 'billing-prepared-contents:mark-draft:' + id
    },
  }

  static BILLING_INVOICES = {
    SEARCH(params: IParams) {
      return "billing-invoices:search:".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: string | null): string {
      return 'billing-invoices:upsert:' + exId
    },
    GET(exId: string): string {
      return 'billing-invoices:get-single:' + exId
    },
    CANCEL(id: string): string {
      return 'billing-invoices:cancel:' + id
    },
    VOID(id: string): string {
      return 'billing-invoices:void:' + id
    },
    FINALIZE(id: string): string {
      return 'billing-invoices:finalize:' + id
    },
  }

  static BILLING_CHECKOUT = {
    CREATE(): string {
      return 'billing-checkout:create'
    }
  }

  static CATEGORYSERIALS = {
    SEARCH(params: IParams) {
      return "categoryserials:search:search".concat(BaseEndpoint.toParamString(params))
    },
    UPSERT(exId: number | null): string {
      return 'categoryserials:upsert:' + exId
    },
    GET(exId: number): string {
      return 'categoryserials:get-single:' + exId
    }
  }

}
